'use client';

import { useTranslations } from 'next-intl';
import { CaretUp } from 'assets/icons';
import { P } from 'components/ring-air-landing/StyleConstants';
import { TravelChargerPricingModal } from 'components/shared/InfoModals/TravelChargerModal';
import gsap from 'gsap';
import CustomLink from 'components/shared/CustomLink';
import { useSearchParams } from 'next/navigation';
import { useContext, useEffect, useRef, useState } from 'react';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import { drawImageSafe } from 'utils/drawImageSafe';
import { getTravelChargerPrice } from 'content/products/accessories';
import {
  CurrencySymbol,
  useFormatPrice,
} from 'hooks/pricing/useFormattedPrice';
import { useGsapContext } from 'hooks/animation/useGsapContext';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import { shopifyCountryMapper } from 'lib/commerce/shopify/shopifyHelper';
import { Container, useModal } from '..';
import { useCartStore } from 'components/shared/Cart';
import { RingSelectionContext } from './RingSelectionContext';
import { Currency } from 'utils/Currency';
import styles from './TravelChargerSequence.module.css';

export const TravelChargerSequence = ({
  className,
  onLoad,
}: {
  className?: string;
  onLoad?: () => void;
}) => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const searchParams = useSearchParams();

  const [screenSize, setScreenSize] = useState<'sm' | 'lg' | null>(null);

  // const frameCount = 286;

  const resolutionMap = {
    sm: {
      tag: 'w_800',
      width: 800,
      height: 450, // TODO: change
      frameStep: 1,
      dir_tag: 'lg', // TODO: changs
      duration: 4,
      frameCount: 115,
    },
    lg: {
      tag: 'w_1920',
      width: 1920,
      height: 1080,
      frameStep: 1,
      dir_tag: 'lg',
      duration: 4,
      frameCount: 115,
    },
  };

  // const getReso = (input: number) => {
  const t = useTranslations('ring');
  //   if (input < 768) return resolutionMap.sm.tag;
  //   if (input < 2000) return resolutionMap.lg.tag;
  //   return resolutionMap.xl.tag;
  // };

  // const getCanvasDims = (
  //   input: number,
  // ): { height: number; width: number } => {
  //   if (input < 768)
  //     return {
  //       width: resolutionMap.sm.width,
  //       height: resolutionMap.sm.height,
  //     };
  //   if (input < 768)
  //     return {
  //       width: resolutionMap.sm.width,
  //       height: resolutionMap.sm.height,
  //     };
  //   if (input < 2000)
  //     return {
  //       width: resolutionMap.lg.width,
  //       height: resolutionMap.lg.height,
  //     };
  //   return { width: resolutionMap.xl.width, height: resolutionMap.xl.height };
  // };

  const animation = useRef<gsap.core.Timeline | null>(null);

  const gsapContext = useGsapContext();

  const intersectionObserver = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    if (window.innerWidth < 768) setScreenSize('sm');
    else setScreenSize('lg');

    const scrollTimeout = setTimeout(() => {
      if (sectionRef.current && searchParams?.get('ps') === 'voyager-charger') {
        window.scrollTo({
          top: sectionRef.current.offsetTop,
          behavior: 'smooth',
        });
      }
    }, 100);

    return () => {
      if (scrollTimeout) clearTimeout(scrollTimeout);
    };
  }, []);

  useIsomorphicLayoutEffect(() => {
    if (!screenSize || !rootRef.current) return;

    animationInit();

    intersectionObserver.current = new IntersectionObserver(
      (e) => {
        if (e[0].intersectionRatio <= 0) {
          if (animation.current) animation.current.pause();
          return;
        }
        if (animation.current) animation.current.play();
      },
      {
        root: null,
        rootMargin: '300px',
        threshold: 0.01,
      },
    );

    intersectionObserver.current.observe(rootRef.current);

    return () => {
      if (intersectionObserver.current) {
        intersectionObserver.current.disconnect();
      }
    };
  }, [screenSize]);

  const animationInit = () => {
    if (!window || !canvas.current || !screenSize) return;

    const frameCount = resolutionMap[screenSize].frameCount;

    // let imageLoadIndex = 0;
    // const onImageLoad = () => {
    //   imageLoadIndex++;
    //   console.log(imageLoadIndex);

    //   if (imageLoadIndex >= images.length - 1 && onLoad) {
    //     animation.current?.play();
    //     (window as any)['animmm'] = animation.current;
    //     onLoad();
    //   }
    // };

    const images: any[] = [];

    const canvasEl = canvas.current;
    const context = canvasEl.getContext('2d');

    canvasEl.width = resolutionMap[screenSize].width;

    canvasEl.height = resolutionMap[screenSize].height;

    const currentFrame = (index: number) =>
      getCompressedAssetUrl(
        `/web_v2/sequence/travel-charger/${(index + 1)
          .toString()
          .padStart(3, '0')}.png/${resolutionMap[screenSize].tag}`,
      );

    if (context) {
      const img = new Image();
      img.src = currentFrame(0);
      img.onload = () => {
        drawImageSafe(context, img, 0, 0);
      };
    }

    const frames = {
      frame: 0,
    };

    for (let i = 0; i < frameCount; i += resolutionMap[screenSize].frameStep) {
      const img = new Image();
      img.loading = 'eager';
      img.src = currentFrame(i);
      images.push(img);
    }

    Promise.all(
      images
        .filter((img) => !img.complete)
        .map(
          (img) =>
            new Promise((resolve) => {
              img.onload = img.onerror = resolve;
            }),
        ),
    ).then(() => {
      if (onLoad) onLoad();
      animation.current?.progress(0);
      animation.current?.play();
      animation.current?.play();
    });

    const render = () => {
      const img = images[frames.frame];
      if (!img?.complete || img.naturalWidth === 0) {
        return;
      }
      if (!context) return;
      requestAnimationFrame(() => {
        context.clearRect(0, 0, canvasEl.width, canvasEl.height);
        drawImageSafe(context, img, 0, 0);
      });
    };

    gsapContext.add(() => {
      animation.current = gsap.timeline({
        defaults: { duration: 1, ease: 'none', reversed: true },
        paused: true,
      });

      animation.current?.fromTo(
        frames,
        {
          frame: 1,
        },
        {
          repeat: -1,
          frame: images.length - 1,
          duration: resolutionMap[screenSize].duration,
          snap: 'frame',
          ease: 'linear',
          onUpdate: render, // use animation onUpdate instead of scrollTrigger's onUpdate
          yoyo: true,
        },
      );
    });
  };

  // const replayAnimation = () => {
  //   if (!animation.current) return;
  //   animation.current.pause();
  //   animation.current.progress(0);
  //   animation.current.play();
  // };

  const ModalClient = useModal();

  const showTravelChargerModal = () => {
    ModalClient.set(<TravelChargerPricingModal />);
    // ModalClient.setCloseButtonTheme('light');
    ModalClient.show();
  };

  // const router = useRouter();

  const { defaultCurrency } = useContext(RingSelectionContext);
  const store = useCartStore((s) => s.store);
  const formatPriceWithComma = useFormatPrice();
  const query = Object.fromEntries(searchParams?.entries() ?? []);
  const travelChargerPrice = getTravelChargerPrice(store);

  return (
    <section
      className={`${styles.root}${className ? ` ${className}` : ''}`}
      id="voyager-charger"
      ref={sectionRef}
    >
      <Container className={styles.sectionContent} ref={rootRef}>
        <div className={styles.textContent}>
          <h2>{t('ring.voyagerCharger.title')}</h2>
          <P>{t('ring.voyagerCharger.subtitle')}</P>
          <div className={styles.ctaContainer}>
            <CustomLink
              className={`${styles.cta} ${styles.primary}`}
              prefetch
              href={{
                pathname: `/shop${
                  store ? '/' + shopifyCountryMapper[store] : ''
                }`,
                query: { ...query },
                hash: 'addons',
              }}
            >
              {t('ring.voyagerCharger.buyNow')}{' '}
              {CurrencySymbol[defaultCurrency.toLowerCase() as Currency]}
              {travelChargerPrice != null && (
                <>{formatPriceWithComma(travelChargerPrice)}</>
              )}
            </CustomLink>
          </div>
          <p className={styles.subText}>
            {t('travelChargerSequence.text.shipsDays')}
          </p>
          <button className={styles.learnMore} onClick={showTravelChargerModal}>
            {t('ring.voyagerCharger.learnMore')}{' '}
            <CaretUp style={{ transform: 'rotate(90deg)' }} />
          </button>
        </div>
        <canvas ref={canvas}></canvas>
      </Container>
    </section>
  );
};
